import React from 'react';

import EasyblocksPage from 'components/storefront/editor/easyblocks-page';
import {
  MESSAGE_EVENTS,
  postEasyblocksCanvasPageRoute,
} from 'components/storefront/editor/utils/post-message';
import {
  isExternalUrl,
  isCheckoutUrl,
  matchPageRouteWithAdapted,
} from 'components/storefront/editor/easyblocks-page/parser/utils';
import { redirectToCheckout } from 'utils/storefront';

export default class StorefrontThemeEditorPage extends React.PureComponent {
  state = {
    loaded: false,
  };

  componentDidMount() {
    // Force light mode in the canvas so admin dark theme is not used into the storefront preview.
    document.documentElement.setAttribute('data-theme', 'light');

    window.navigation?.addEventListener('navigate', this.handleUrlChange);
    window.setCanvasData = this.setCanvasData.bind(this);
    window.setThemeGlobals = this.setThemeGlobals.bind(this);
    window.parent.postMessage(
      { type: MESSAGE_EVENTS.STOREFRONT_EDITOR.CANVAS.MOUNTED },
      '*',
    );

    this.removeLoading();
  }

  componentWillUnmount() {
    window.navigation?.removeEventListener('navigate', this.handleUrlChange);
  }

  handleUrlChange = async (event) => {
    // try displaying the corresponding page in the editor if the canvas is navigated from scripts
    const { theme } = this.state;
    const href = event.destination?.url;

    if (!href || !theme) {
      return;
    }

    const url = new URL(href);
    const { pageId, recordId } = matchPageRouteWithAdapted(
      url.pathname,
      this.state.theme,
    );

    if (pageId) {
      event.preventDefault();

      const searchResult = await this.handleSearchRequest(url, pageId);
      if (searchResult) {
        return;
      }

      postEasyblocksCanvasPageRoute(pageId, window.parent, {
        record: recordId,
      });

      return;
    }

    // open external links in a separate tab
    if (isExternalUrl(href)) {
      event.preventDefault();
      window.parent.open(href, '_blank').focus();
      return;
    }

    // open hosted checkout in the checkout editor
    if (isCheckoutUrl(href)) {
      event.preventDefault();
      redirectToCheckout(href);
    }
  };

  async handleSearchRequest(url, pageId) {
    if (pageId !== 'search') {
      return false;
    }

    if (!url.search) {
      return false;
    }

    // handle only query changes
    if (this.state.pageId !== 'search') {
      return false;
    }

    // we should find the main search form and define its section id
    const searchForms = document.querySelectorAll('form[action="/search"]');
    let section = null;
    for (const searchForm of searchForms) {
      section = searchForm.closest('.section');
      if (section) {
        break;
      }
    }

    const sectionId = section?.id;
    if (!sectionId) {
      return false;
    }

    try {
      // get and replace section content
      const response = await fetch(`${url.pathname}${url.search}`);
      const responseText = await response.text();

      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const newSection = html.getElementById(sectionId);
      if (newSection) {
        section.innerHTML = newSection.innerHTML;
      }
    } catch (err) {
      console.log('error during search action', err);
      return false;
    }

    return true;
  }

  setCanvasData(canvasData) {
    this.setState({
      ...canvasData,
      loaded: true,
    });
  }

  setThemeGlobals(globals) {
    const themeGlobals = globals || this.state.theme.globals;

    const pageProps = {
      ...(this.state.pageProps || undefined),
      themeGlobals: {
        ...themeGlobals,
      },
    };

    this.setState({
      pageProps,
      themeGlobals: {
        ...themeGlobals,
      },
    });
  }

  removeLoading() {
    const loadingElement = document.getElementById('loading');

    if (loadingElement) {
      loadingElement.remove();
    }
  }

  render() {
    const {
      loaded,
      theme,
      pageProps,
      pageContent,
      layoutProps,
      allSections,
      pageSections,
      layoutSectionGroups,
      sectionGroups,
      themeGlobals,
      pageId,
      record,
    } = this.state;

    if (!loaded) {
      return null;
    }

    return (
      <EasyblocksPage
        pageId={pageId}
        pageProps={pageProps}
        pageContent={pageContent}
        layoutProps={layoutProps}
        allSections={allSections}
        pageSections={pageSections}
        layoutSectionGroups={layoutSectionGroups}
        sectionGroups={sectionGroups}
        themeGlobals={themeGlobals}
        theme={theme}
        record={record}
      />
    );
  }
}
